import { create } from 'zustand';

interface Fee {
  category: string;
  created_at: string;
  currency: string;
  value: number;
  name: string;
}

interface FeeGroup {
  [key: string]: Fee[];
}

interface Client {
  _id?: string;
  name: string;
  email: string;
  phone: string;
  address: string;
  document: string;
  documentType: 'CPF' | 'CNPJ';
}

interface ProposalData {
  quotationNo: string;
  from: string;
  to: string;
  mode: string;
  date: string;
  expiryDate: string;
  containerType: string;
  grossWeight: string;
  incoTerms: string;
  carrier: string;
  route: string;
  cargoType: string;
  serviceType: string;
  transitTime: string;
  sailingDate: string;
  freeDays: number;
  commodity: string;
  terms: string;
}

interface ProposalStore {
  fees: FeeGroup;
  originalFees: FeeGroup;
  clients: Client[];
  selectedClient: Client | null;
  logo: string;
  proposalData: ProposalData;

  // NOVO: Estados e ação para cotação
  exchangeRates: Record<string, number>;
  fetchExchangeRates: () => Promise<void>;

  setFees: (fees: FeeGroup) => void;
  updateFeeValue: (group: string, index: number, newValue: number) => void;
  addFee: (group: string, fee: Fee) => void;
  deleteFee: (group: string, index: number) => void;
  addClient: (client: Client) => void;
  setSelectedClient: (client: Client | null) => void;
  clearClient: () => void;
  setLogo: (logo: string) => void;
  setProposalData: (data: ProposalData) => void;
  updateProposalField: (field: keyof ProposalData, value: any) => void;
  clearProposalData: () => void;
  resetFeesToOriginal: () => void;
  clearAll: () => void;
  getTotalValue: () => number;
}

const defaultProposalData: ProposalData = {
  quotationNo: '',
  from: '',
  to: '',
  mode: '',
  date: '',
  expiryDate: '',
  containerType: '',
  grossWeight: '',
  incoTerms: '',
  carrier: '',
  route: '',
  cargoType: '',
  serviceType: 'CY/CY',
  transitTime: '',
  sailingDate: '',
  freeDays: 0,
  commodity: '',
  terms: ''
};

export const useProposalStore = create<ProposalStore>((set, get) => ({
  fees: {},
  originalFees: {},
  clients: [],
  selectedClient: null,
  logo: '',
  proposalData: { ...defaultProposalData },

  // Cotações iniciais, valores padrão antes de buscar online
  exchangeRates: {
    USD: 1,
    EUR: 1.1,
    BRL: 0.18,
    GBP: 1.25,
    CNY: 0.14,
    JPY: 0.007,
  },

  // Busca cotação em tempo real
  fetchExchangeRates: async () => {
    try {
      const res = await fetch('https://api.exchangerate.host/latest?base=USD');
      const data = await res.json();
      if (data && data.rates) {
        set({ exchangeRates: { ...data.rates, USD: 1 } });
      }
    } catch (err) {
      console.error('Erro ao buscar taxas de câmbio:', err);
      // Se der erro, continua usando o cache/local
    }
  },

  setFees: (fees) => set((state) => ({
    fees,
    originalFees: Object.keys(state.originalFees).length === 0 ? { ...fees } : state.originalFees
  })),

  updateFeeValue: (group, index, newValue) => set((state) => ({
    fees: {
      ...state.fees,
      [group]: state.fees[group].map((fee, i) =>
        i === index ? { ...fee, value: newValue } : fee
      )
    }
  })),

  addFee: (group, fee) => set((state) => ({
    fees: {
      ...state.fees,
      [group]: [...(state.fees[group] || []), fee]
    }
  })),

  deleteFee: (group, index) => set((state) => {
    const newFees = { ...state.fees };
    newFees[group] = newFees[group].filter((_, i) => i !== index);
    if (newFees[group].length === 0) delete newFees[group];
    return { fees: newFees };
  }),

  clearClient: () => set({ clients: [] }),

  addClient: (client) => set((state) => ({
    clients: [...state.clients, { ...client }]
  })),

  setSelectedClient: (client) => set({ selectedClient: client }),

  setLogo: (logo) => set({ logo }),

  setProposalData: (data) => set({ proposalData: { ...data } }),

  updateProposalField: (field, value) => set((state) => ({
    proposalData: { ...state.proposalData, [field]: value }
  })),

  clearProposalData: () => set({ proposalData: { ...defaultProposalData } }),

  resetFeesToOriginal: () => set((state) => ({
    fees: { ...state.originalFees }
  })),

  clearAll: () => set({
    fees: {},
    originalFees: {},
    clients: [],
    selectedClient: null,
    logo: '',
    proposalData: { ...defaultProposalData },
    exchangeRates: {
      USD: 1,
      EUR: 1.1,
      BRL: 0.18,
      GBP: 1.25,
      CNY: 0.14,
      JPY: 0.007,
    }
  }),

  // Agora usa cotação em tempo real para converter para USD!
  getTotalValue: () => {
    const state = get();
    let total = 0;
    const rates = state.exchangeRates || {};

    Object.values(state.fees).forEach(feeGroup => {
      feeGroup.forEach(fee => {
        // Conversão real: se for USD soma direto, senão divide pela cotação da moeda (converte tudo para USD)
        if (fee.currency === 'USD') {
          total += fee.value;
        } else if (rates[fee.currency]) {
          total += fee.value / rates[fee.currency];
        } else {
          // fallback hardcoded se faltar alguma moeda na cotação
          total +=
            fee.currency === 'EUR' ? fee.value * 1.1 :
              fee.currency === 'BRL' ? fee.value * 0.18 :
                fee.currency === 'GBP' ? fee.value * 1.25 :
                  fee.currency === 'CNY' ? fee.value * 0.14 :
                    fee.currency === 'JPY' ? fee.value * 0.007 :
                      fee.value;
        }
      });
    });

    return total;
  }
}));

// Mantém igual:
export const useProposalActions = () => {
  const store = useProposalStore();
  return {
    setFees: store.setFees,
    updateFeeValue: store.updateFeeValue,
    addFee: store.addFee,
    deleteFee: store.deleteFee,
    resetFeesToOriginal: store.resetFeesToOriginal,
    addClient: store.addClient,
    setSelectedClient: store.setSelectedClient,
    clearClient: store.clearClient,
    setProposalData: store.setProposalData,
    updateProposalField: store.updateProposalField,
    clearProposalData: store.clearProposalData,
    setLogo: store.setLogo,
    clearAll: store.clearAll,
    getTotalValue: store.getTotalValue,
    fetchExchangeRates: store.fetchExchangeRates, // NOVO
  };
};

export const useProposalData = () => {
  const store = useProposalStore();
  return {
    fees: store.fees,
    originalFees: store.originalFees,
    clients: store.clients,
    selectedClient: store.selectedClient,
    logo: store.logo,
    proposalData: store.proposalData,
    exchangeRates: store.exchangeRates, // NOVO
    totalValue: store.getTotalValue()
  };
};

export const useFeesOnly = () => useProposalStore(state => state.fees);
export const useClientsOnly = () => useProposalStore(state => state.clients);
export const useSelectedClientOnly = () => useProposalStore(state => state.selectedClient);
export const useProposalDataOnly = () => useProposalStore(state => state.proposalData);
export const useLogoOnly = () => useProposalStore(state => state.logo);
export const useExchangeRates = () => useProposalStore(state => state.exchangeRates);
